// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

// ================================================================
// GuardRok — Translations
// Add new languages here. Keys must match across all language objects.
// ================================================================

export type LangCode = "en" | "de" | "fr" | "es" | "pt" | "nl" | "it" | "ja" | "zh" | "ko" | "hi" | "ar" | "sw"

export const LANGUAGES: { code: LangCode; label: string; flag: string; dir?: "rtl" }[] = [
  { code: "en", label: "English",    flag: "🇺🇸" },
  { code: "de", label: "Deutsch",    flag: "🇩🇪" },
  { code: "fr", label: "Français",   flag: "🇫🇷" },
  { code: "es", label: "Español",    flag: "🇪🇸" },
  { code: "pt", label: "Português",  flag: "🇧🇷" },
  { code: "nl", label: "Nederlands", flag: "🇳🇱" },
  { code: "it", label: "Italiano",   flag: "🇮🇹" },
  { code: "ja", label: "日本語",      flag: "🇯🇵" },
  { code: "zh", label: "中文",        flag: "🇨🇳" },
  { code: "ko", label: "한국어",      flag: "🇰🇷" },
  { code: "hi", label: "हिन्दी",      flag: "🇮🇳" },
  { code: "ar", label: "العربية",    flag: "🇸🇦", dir: "rtl" },
  { code: "sw", label: "Kiswahili",  flag: "🇰🇪" },
]

export type TranslationKeys = {
  // Nav
  nav_docs: string
  nav_use_cases: string
  nav_pricing: string
  nav_signin: string
  nav_start_free: string
  nav_demo: string

  // Hero
  hero_badge: string
  hero_h1_line1: string
  hero_h1_line2: string
  hero_subtitle: string
  hero_cta_primary: string
  hero_cta_secondary: string
  hero_cta_demo: string
  hero_social_proof: string

  // How it works
  how_title: string
  how_step1_title: string
  how_step1_desc: string
  how_step2_title: string
  how_step2_desc: string
  how_step3_title: string
  how_step3_desc: string

  // Features
  features_title: string
  feat_phi: string
  feat_phi_desc: string
  feat_pii: string
  feat_pii_desc: string
  feat_financial: string
  feat_financial_desc: string
  feat_api: string
  feat_api_desc: string
  feat_real_estate: string
  feat_real_estate_desc: string
  feat_confidential: string
  feat_confidential_desc: string

  // Stats
  stat_block: string
  stat_block_label: string
  stat_tools: string
  stat_tools_label: string
  stat_store: string
  stat_store_label: string
  stat_privacy: string
  stat_privacy_label: string

  // CTA section
  cta_title: string
  cta_subtitle: string
  cta_button: string

  // Footer
  footer_tagline: string
  footer_legal: string
}

const t: Record<LangCode, TranslationKeys> = {
  en: {
    nav_docs: "Docs", nav_use_cases: "Use Cases", nav_pricing: "Pricing", nav_signin: "Sign in", nav_start_free: "Start free", nav_demo: "Book demo",
    hero_badge: "No IT setup required · Deploys in 10 min · No credit card",
    hero_h1_line1: "Your employees are leaking",
    hero_h1_line2: "data to AI. Stop it.",
    hero_subtitle: "GuardRok™ intercepts prompts before they reach ChatGPT, Copilot, Gemini, or Claude — blocking PHI, PII, financial data, and secrets in real time.",
    hero_cta_primary: "Start free — no card needed",
    hero_cta_secondary: "See how it works",
    hero_cta_demo: "Book a demo",
    hero_social_proof: "Early access customers — regulated organisations where one wrong prompt costs more than the product.",
    how_title: "How GuardRok works",
    how_step1_title: "Employee types a prompt",
    how_step1_desc: "In ChatGPT, Copilot, Gemini, or Claude on their work browser.",
    how_step2_title: "GuardRok intercepts it",
    how_step2_desc: "Before it reaches the AI tool — in real time. No prompt is ever stored or shared.",
    how_step3_title: "Block, redact, or warn",
    how_step3_desc: "Based on your organisation's policies. Logs the event. Never stores the prompt.",
    features_title: "What GuardRok protects",
    feat_phi: "Patient Health Information", feat_phi_desc: "HIPAA / HITECH compliant blocking of PHI before it reaches any AI tool.",
    feat_pii: "Personal Data", feat_pii_desc: "Names, emails, IDs — GDPR and CCPA aligned detection and blocking.",
    feat_financial: "Financial Data", feat_financial_desc: "Account numbers, trading data, earnings — SOX, FINRA, PCI-DSS coverage.",
    feat_api: "API Keys & Secrets", feat_api_desc: "Credentials, tokens, and passwords intercepted before reaching AI models.",
    feat_real_estate: "Real Estate Data", feat_real_estate_desc: "Escrow details, wire instructions, client SSNs — RESPA and Fair Housing Act.",
    feat_confidential: "Confidential Content", feat_confidential_desc: "Attorney-client privilege, M&A data, classified contracts — blocked by policy.",
    stat_block: "Real-time", stat_block_label: "Interception", stat_tools: "4", stat_tools_label: "AI Tools covered",
    stat_store: "0", stat_store_label: "Prompt text stored", stat_privacy: "< 10 min", stat_privacy_label: "Setup time",
    cta_title: "Start protecting your team today", cta_subtitle: "Free for 2 seats — limited time introductory offer. No credit card required.", cta_button: "Get started free",
    footer_tagline: "AI Firewall for regulated teams.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  de: {
    nav_docs: "Dokumentation", nav_use_cases: "Anwendungsfälle", nav_pricing: "Preise", nav_signin: "Anmelden", nav_start_free: "Kostenlos starten", nav_demo: "Demo buchen",
    hero_badge: "Keine IT-Einrichtung · Einsatzbereit in 10 Min · Keine Kreditkarte",
    hero_h1_line1: "Ihre Mitarbeiter geben Daten",
    hero_h1_line2: "an KI weiter. Stoppen Sie das.",
    hero_subtitle: "GuardRok™ fängt Prompts ab, bevor sie ChatGPT, Copilot, Gemini oder Claude erreichen — blockiert PHI, personenbezogene Daten und Geheimnisse in Echtzeit.",
    hero_cta_primary: "Kostenlos starten — keine Karte nötig",
    hero_cta_secondary: "So funktioniert es",
    hero_cta_demo: "Demo buchen",
    hero_social_proof: "Frühe Kunden — regulierte Organisationen, bei denen ein falscher Prompt mehr kostet als das Produkt.",
    how_title: "Wie GuardRok funktioniert",
    how_step1_title: "Mitarbeiter tippt einen Prompt",
    how_step1_desc: "In ChatGPT, Copilot, Gemini oder Claude im Arbeitsbrowser.",
    how_step2_title: "GuardRok fängt ihn ab",
    how_step2_desc: "Bevor er das KI-Tool erreicht — in Echtzeit. Kein Prompt wird jemals gespeichert.",
    how_step3_title: "Blockieren, schwärzen oder warnen",
    how_step3_desc: "Basierend auf den Richtlinien Ihrer Organisation. Protokolliert das Ereignis. Speichert den Prompt nie.",
    features_title: "Was GuardRok schützt",
    feat_phi: "Patientengesundheitsdaten", feat_phi_desc: "HIPAA/HITECH-konforme Blockierung von PHI vor KI-Tools.",
    feat_pii: "Personenbezogene Daten", feat_pii_desc: "Namen, E-Mails, IDs — DSGVO- und CCPA-konforme Erkennung.",
    feat_financial: "Finanzdaten", feat_financial_desc: "Kontonummern, Handelsdaten — SOX, FINRA, PCI-DSS Abdeckung.",
    feat_api: "API-Schlüssel & Geheimnisse", feat_api_desc: "Zugangsdaten, Tokens und Passwörter abgefangen.",
    feat_real_estate: "Immobiliendaten", feat_real_estate_desc: "Treuhanddaten, Überweisungsanweisungen — RESPA-konform.",
    feat_confidential: "Vertrauliche Inhalte", feat_confidential_desc: "Anwalt-Mandant-Privileg, M&A-Daten — durch Richtlinien blockiert.",
    stat_block: "Echtzeit", stat_block_label: "Abfangung", stat_tools: "4", stat_tools_label: "KI-Tools abgedeckt",
    stat_store: "0", stat_store_label: "Prompt-Text gespeichert", stat_privacy: "< 10 Min", stat_privacy_label: "Einrichtungszeit",
    cta_title: "Schützen Sie Ihr Team noch heute", cta_subtitle: "Kostenlos für 2 Plätze — zeitlich begrenztes Einführungsangebot. Keine Kreditkarte.", cta_button: "Kostenlos starten",
    footer_tagline: "KI-Firewall für regulierte Teams.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  fr: {
    nav_docs: "Documentation", nav_use_cases: "Cas d'usage", nav_pricing: "Tarifs", nav_signin: "Connexion", nav_start_free: "Commencer gratuitement", nav_demo: "Réserver une démo",
    hero_badge: "Aucune configuration IT · Opérationnel en 10 min · Sans carte bancaire",
    hero_h1_line1: "Vos employés transmettent des données",
    hero_h1_line2: "à l'IA. Arrêtez ça.",
    hero_subtitle: "GuardRok™ intercepte les invites avant qu'elles n'atteignent ChatGPT, Copilot, Gemini ou Claude — bloquant PHI, données personnelles et secrets en temps réel.",
    hero_cta_primary: "Commencer gratuitement — sans carte",
    hero_cta_secondary: "Comment ça marche",
    hero_cta_demo: "Réserver une démo",
    hero_social_proof: "Clients en accès anticipé — organisations réglementées où une mauvaise invite coûte plus que le produit.",
    how_title: "Comment fonctionne GuardRok",
    how_step1_title: "Un employé tape une invite",
    how_step1_desc: "Dans ChatGPT, Copilot, Gemini ou Claude sur son navigateur professionnel.",
    how_step2_title: "GuardRok l'intercepte",
    how_step2_desc: "Avant qu'il n'atteigne l'outil IA — en temps réel. Aucun prompt n'est jamais stocké.",
    how_step3_title: "Bloquer, expurger ou avertir",
    how_step3_desc: "Selon les politiques de votre organisation. Journalise l'événement. Ne stocke jamais l'invite.",
    features_title: "Ce que GuardRok protège",
    feat_phi: "Données de santé", feat_phi_desc: "Blocage conforme HIPAA/HITECH des données de santé avant tout outil IA.",
    feat_pii: "Données personnelles", feat_pii_desc: "Noms, e-mails, identifiants — détection conforme RGPD et CCPA.",
    feat_financial: "Données financières", feat_financial_desc: "Numéros de compte, données de trading — couverture SOX, FINRA, PCI-DSS.",
    feat_api: "Clés API & Secrets", feat_api_desc: "Identifiants, tokens et mots de passe interceptés.",
    feat_real_estate: "Données immobilières", feat_real_estate_desc: "Données de séquestre, instructions de virement — conformité RESPA.",
    feat_confidential: "Contenu confidentiel", feat_confidential_desc: "Secret professionnel, données M&A — bloqués par politique.",
    stat_block: "Temps réel", stat_block_label: "Interception", stat_tools: "4", stat_tools_label: "Outils IA couverts",
    stat_store: "0", stat_store_label: "Texte d'invite stocké", stat_privacy: "< 10 min", stat_privacy_label: "Temps de déploiement",
    cta_title: "Protégez votre équipe dès aujourd'hui", cta_subtitle: "Gratuit pour 2 postes — offre d'introduction limitée. Sans carte bancaire.", cta_button: "Commencer gratuitement",
    footer_tagline: "Pare-feu IA pour les équipes réglementées.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  es: {
    nav_docs: "Documentación", nav_use_cases: "Casos de uso", nav_pricing: "Precios", nav_signin: "Iniciar sesión", nav_start_free: "Empezar gratis", nav_demo: "Reservar demo",
    hero_badge: "Sin configuración IT · Listo en 10 min · Sin tarjeta",
    hero_h1_line1: "Sus empleados están filtrando datos",
    hero_h1_line2: "a la IA. Deténgalo.",
    hero_subtitle: "GuardRok™ intercepta los prompts antes de que lleguen a ChatGPT, Copilot, Gemini o Claude — bloqueando PHI, datos personales y secretos en tiempo real.",
    hero_cta_primary: "Empezar gratis — sin tarjeta",
    hero_cta_secondary: "Ver cómo funciona",
    hero_cta_demo: "Reservar una demo",
    hero_social_proof: "Clientes en acceso anticipado — organizaciones reguladas donde un prompt incorrecto cuesta más que el producto.",
    how_title: "Cómo funciona GuardRok",
    how_step1_title: "El empleado escribe un prompt",
    how_step1_desc: "En ChatGPT, Copilot, Gemini o Claude en su navegador de trabajo.",
    how_step2_title: "GuardRok lo intercepta",
    how_step2_desc: "Antes de que llegue a la herramienta de IA — en tiempo real. Ningún prompt se almacena jamás.",
    how_step3_title: "Bloquear, redactar o advertir",
    how_step3_desc: "Según las políticas de su organización. Registra el evento. Nunca almacena el prompt.",
    features_title: "Lo que GuardRok protege",
    feat_phi: "Información de salud", feat_phi_desc: "Bloqueo de PHI conforme a HIPAA/HITECH antes de llegar a cualquier IA.",
    feat_pii: "Datos personales", feat_pii_desc: "Nombres, correos, IDs — detección alineada con RGPD y CCPA.",
    feat_financial: "Datos financieros", feat_financial_desc: "Números de cuenta, datos bursátiles — cobertura SOX, FINRA, PCI-DSS.",
    feat_api: "Claves API y secretos", feat_api_desc: "Credenciales, tokens y contraseñas interceptados.",
    feat_real_estate: "Datos inmobiliarios", feat_real_estate_desc: "Datos de depósito en garantía, instrucciones de transferencia — RESPA.",
    feat_confidential: "Contenido confidencial", feat_confidential_desc: "Privilegio abogado-cliente, datos M&A — bloqueados por política.",
    stat_block: "Tiempo real", stat_block_label: "Intercepción", stat_tools: "4", stat_tools_label: "Herramientas IA",
    stat_store: "0", stat_store_label: "Texto de prompt almacenado", stat_privacy: "< 10 min", stat_privacy_label: "Tiempo de despliegue",
    cta_title: "Empiece a proteger su equipo hoy", cta_subtitle: "Gratis para 2 puestos — oferta de introducción por tiempo limitado. Sin tarjeta.", cta_button: "Empezar gratis",
    footer_tagline: "Cortafuegos IA para equipos regulados.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  pt: {
    nav_docs: "Documentação", nav_use_cases: "Casos de uso", nav_pricing: "Preços", nav_signin: "Entrar", nav_start_free: "Começar grátis", nav_demo: "Agendar demo",
    hero_badge: "Sem configuração de TI · Pronto em 10 min · Sem cartão",
    hero_h1_line1: "Seus funcionários estão vazando dados",
    hero_h1_line2: "para a IA. Pare isso.",
    hero_subtitle: "GuardRok™ intercepta prompts antes de chegarem ao ChatGPT, Copilot, Gemini ou Claude — bloqueando PHI, dados pessoais e segredos em tempo real.",
    hero_cta_primary: "Começar grátis — sem cartão",
    hero_cta_secondary: "Ver como funciona",
    hero_cta_demo: "Agendar uma demo",
    hero_social_proof: "Clientes em acesso antecipado — organizações reguladas onde um prompt errado custa mais que o produto.",
    how_title: "Como o GuardRok funciona",
    how_step1_title: "Funcionário digita um prompt",
    how_step1_desc: "No ChatGPT, Copilot, Gemini ou Claude no navegador de trabalho.",
    how_step2_title: "GuardRok intercepta",
    how_step2_desc: "Antes de chegar à ferramenta de IA — em tempo real. Nenhum prompt é armazenado.",
    how_step3_title: "Bloquear, redigir ou alertar",
    how_step3_desc: "Com base nas políticas da sua organização. Registra o evento. Nunca armazena o prompt.",
    features_title: "O que o GuardRok protege",
    feat_phi: "Dados de saúde", feat_phi_desc: "Bloqueio de PHI em conformidade com HIPAA/HITECH antes de qualquer IA.",
    feat_pii: "Dados pessoais", feat_pii_desc: "Nomes, e-mails, IDs — detecção alinhada à LGPD e GDPR.",
    feat_financial: "Dados financeiros", feat_financial_desc: "Números de conta, dados de negociação — cobertura SOX, FINRA, PCI-DSS.",
    feat_api: "Chaves de API e segredos", feat_api_desc: "Credenciais, tokens e senhas interceptados.",
    feat_real_estate: "Dados imobiliários", feat_real_estate_desc: "Dados de custódia, instruções de transferência — conformidade RESPA.",
    feat_confidential: "Conteúdo confidencial", feat_confidential_desc: "Privilégio advogado-cliente, dados de M&A — bloqueados por política.",
    stat_block: "Tempo real", stat_block_label: "Interceptação", stat_tools: "4", stat_tools_label: "Ferramentas de IA",
    stat_store: "0", stat_store_label: "Texto de prompt armazenado", stat_privacy: "< 10 min", stat_privacy_label: "Tempo de implantação",
    cta_title: "Comece a proteger sua equipe hoje", cta_subtitle: "Grátis para 2 assentos — oferta introdutória por tempo limitado. Sem cartão.", cta_button: "Começar grátis",
    footer_tagline: "Firewall de IA para equipes reguladas.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  nl: {
    nav_docs: "Documentatie", nav_use_cases: "Gebruiksscenario's", nav_pricing: "Prijzen", nav_signin: "Inloggen", nav_start_free: "Gratis starten", nav_demo: "Demo boeken",
    hero_badge: "Geen IT-installatie · Actief in 10 min · Geen creditcard",
    hero_h1_line1: "Uw medewerkers lekken gegevens",
    hero_h1_line2: "naar AI. Stop dat.",
    hero_subtitle: "GuardRok™ onderschept prompts voordat ze ChatGPT, Copilot, Gemini of Claude bereiken — blokkeert PHI, persoonsgegevens en geheimen in realtime.",
    hero_cta_primary: "Gratis starten — geen kaart nodig",
    hero_cta_secondary: "Zie hoe het werkt",
    hero_cta_demo: "Demo boeken",
    hero_social_proof: "Vroege gebruikers — gereguleerde organisaties waar één verkeerde prompt meer kost dan het product.",
    how_title: "Hoe GuardRok werkt",
    how_step1_title: "Medewerker typt een prompt",
    how_step1_desc: "In ChatGPT, Copilot, Gemini of Claude op de werkbrowser.",
    how_step2_title: "GuardRok onderschept het",
    how_step2_desc: "Voordat het de AI-tool bereikt — in realtime. Geen prompt wordt ooit opgeslagen.",
    how_step3_title: "Blokkeren, redigeren of waarschuwen",
    how_step3_desc: "Op basis van uw organisatiebeleid. Logt de gebeurtenis. Slaat de prompt nooit op.",
    features_title: "Wat GuardRok beschermt",
    feat_phi: "Patiëntgezondheidsgegevens", feat_phi_desc: "HIPAA/HITECH-conforme blokkering van PHI vóór AI-tools.",
    feat_pii: "Persoonsgegevens", feat_pii_desc: "Namen, e-mails, ID's — AVG- en CCPA-detectie.",
    feat_financial: "Financiële gegevens", feat_financial_desc: "Rekeningnummers, handelsgegevens — SOX, FINRA, PCI-DSS.",
    feat_api: "API-sleutels & geheimen", feat_api_desc: "Inloggegevens, tokens en wachtwoorden onderschept.",
    feat_real_estate: "Vastgoedgegevens", feat_real_estate_desc: "Derdenrekeningen, overboekingsinstructies — RESPA.",
    feat_confidential: "Vertrouwelijke inhoud", feat_confidential_desc: "Advocaat-cliëntprivilege, M&A-gegevens — geblokkeerd door beleid.",
    stat_block: "Realtime", stat_block_label: "Onderschepping", stat_tools: "4", stat_tools_label: "AI-tools gedekt",
    stat_store: "0", stat_store_label: "Prompttekst opgeslagen", stat_privacy: "< 10 min", stat_privacy_label: "Installatietijd",
    cta_title: "Begin vandaag uw team te beschermen", cta_subtitle: "Gratis voor 2 gebruikers — beperkt introductieaanbod. Geen creditcard.", cta_button: "Gratis aan de slag",
    footer_tagline: "AI-firewall voor gereguleerde teams.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  it: {
    nav_docs: "Documentazione", nav_use_cases: "Casi d'uso", nav_pricing: "Prezzi", nav_signin: "Accedi", nav_start_free: "Inizia gratis", nav_demo: "Prenota demo",
    hero_badge: "Nessuna configurazione IT · Attivo in 10 min · Nessuna carta",
    hero_h1_line1: "I tuoi dipendenti stanno condividendo dati",
    hero_h1_line2: "con l'IA. Fermalo.",
    hero_subtitle: "GuardRok™ intercetta i prompt prima che raggiungano ChatGPT, Copilot, Gemini o Claude — bloccando PHI, dati personali e segreti in tempo reale.",
    hero_cta_primary: "Inizia gratis — senza carta",
    hero_cta_secondary: "Come funziona",
    hero_cta_demo: "Prenota una demo",
    hero_social_proof: "Clienti in accesso anticipato — organizzazioni regolamentate dove un prompt sbagliato costa più del prodotto.",
    how_title: "Come funziona GuardRok",
    how_step1_title: "Il dipendente digita un prompt",
    how_step1_desc: "In ChatGPT, Copilot, Gemini o Claude nel browser di lavoro.",
    how_step2_title: "GuardRok lo intercetta",
    how_step2_desc: "Prima che raggiunga lo strumento AI — in tempo reale. Nessun prompt viene mai archiviato.",
    how_step3_title: "Blocca, oscura o avvisa",
    how_step3_desc: "In base alle policy della tua organizzazione. Registra l'evento. Non archivia mai il prompt.",
    features_title: "Cosa protegge GuardRok",
    feat_phi: "Dati sanitari", feat_phi_desc: "Blocco PHI conforme a HIPAA/HITECH prima di qualsiasi strumento IA.",
    feat_pii: "Dati personali", feat_pii_desc: "Nomi, email, ID — rilevamento allineato a GDPR e CCPA.",
    feat_financial: "Dati finanziari", feat_financial_desc: "Numeri di conto, dati di trading — copertura SOX, FINRA, PCI-DSS.",
    feat_api: "Chiavi API e segreti", feat_api_desc: "Credenziali, token e password intercettati.",
    feat_real_estate: "Dati immobiliari", feat_real_estate_desc: "Dati di deposito, istruzioni di bonifico — conformità RESPA.",
    feat_confidential: "Contenuto riservato", feat_confidential_desc: "Privilegio avvocato-cliente, dati M&A — bloccati da policy.",
    stat_block: "Tempo reale", stat_block_label: "Intercettazione", stat_tools: "4", stat_tools_label: "Strumenti IA coperti",
    stat_store: "0", stat_store_label: "Testo prompt archiviato", stat_privacy: "< 10 min", stat_privacy_label: "Tempo di installazione",
    cta_title: "Inizia a proteggere il tuo team oggi", cta_subtitle: "Gratuito per 2 posti — offerta introduttiva a tempo limitato. Senza carta di credito.", cta_button: "Inizia gratis",
    footer_tagline: "Firewall IA per team regolamentati.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  ja: {
    nav_docs: "ドキュメント", nav_use_cases: "ユースケース", nav_pricing: "料金", nav_signin: "ログイン", nav_start_free: "無料で始める", nav_demo: "デモを予約",
    hero_badge: "IT設定不要 · 10分で導入 · クレジットカード不要",
    hero_h1_line1: "従業員がAIにデータを",
    hero_h1_line2: "漏洩しています。今すぐ止めましょう。",
    hero_subtitle: "GuardRok™はプロンプトがChatGPT、Copilot、Gemini、Claudeに到達する前に傍受し、PHI、個人情報、財務データをリアルタイムでブロックします。",
    hero_cta_primary: "無料で始める — カード不要",
    hero_cta_secondary: "仕組みを見る",
    hero_cta_demo: "デモを予約する",
    hero_social_proof: "早期アクセスのお客様 — 一つの誤ったプロンプトが製品より高くつく規制された組織。",
    how_title: "GuardRokの仕組み",
    how_step1_title: "従業員がプロンプトを入力",
    how_step1_desc: "職場のブラウザでChatGPT、Copilot、Gemini、またはClaude上で。",
    how_step2_title: "GuardRokが傍受",
    how_step2_desc: "AIツールに届く前にリアルタイムで。プロンプトは一切保存されません。",
    how_step3_title: "ブロック、編集、または警告",
    how_step3_desc: "組織のポリシーに基づいて。イベントを記録。プロンプトは保存しません。",
    features_title: "GuardRokが保護するもの",
    feat_phi: "患者健康情報", feat_phi_desc: "HIPAA/HITECH準拠のPHIブロッキング。",
    feat_pii: "個人情報", feat_pii_desc: "氏名、メール、ID — GDPR・CCPA準拠の検出。",
    feat_financial: "財務データ", feat_financial_desc: "口座番号、取引データ — SOX、FINRA、PCI-DSS対応。",
    feat_api: "APIキーと秘密情報", feat_api_desc: "認証情報、トークン、パスワードを傍受。",
    feat_real_estate: "不動産データ", feat_real_estate_desc: "エスクロー詳細、送金指示 — RESPA準拠。",
    feat_confidential: "機密コンテンツ", feat_confidential_desc: "弁護士・依頼人の特権、M&Aデータ — ポリシーでブロック。",
    stat_block: "リアルタイム", stat_block_label: "傍受", stat_tools: "4", stat_tools_label: "対応AIツール",
    stat_store: "0", stat_store_label: "保存プロンプトテキスト", stat_privacy: "< 10分", stat_privacy_label: "導入時間",
    cta_title: "今日からチームを守りましょう", cta_subtitle: "2席まで無料 — 期間限定の初回特別オファー。クレジットカード不要。", cta_button: "無料で始める",
    footer_tagline: "規制されたチームのためのAIファイアウォール。", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  zh: {
    nav_docs: "文档", nav_use_cases: "使用案例", nav_pricing: "定价", nav_signin: "登录", nav_start_free: "免费开始", nav_demo: "预约演示",
    hero_badge: "无需IT配置 · 10分钟部署 · 无需信用卡",
    hero_h1_line1: "您的员工正在向AI",
    hero_h1_line2: "泄露数据。立即阻止。",
    hero_subtitle: "GuardRok™在提示词到达ChatGPT、Copilot、Gemini或Claude之前拦截它们——实时阻止PHI、个人数据和机密信息。",
    hero_cta_primary: "免费开始 — 无需信用卡",
    hero_cta_secondary: "了解工作原理",
    hero_cta_demo: "预约演示",
    hero_social_proof: "早期客户 — 受监管的组织，一个错误的提示词代价比产品更高。",
    how_title: "GuardRok如何工作",
    how_step1_title: "员工输入提示词",
    how_step1_desc: "在工作浏览器中的ChatGPT、Copilot、Gemini或Claude上。",
    how_step2_title: "GuardRok拦截",
    how_step2_desc: "在到达AI工具之前 — 实时拦截。提示词内容从不被存储。",
    how_step3_title: "阻止、编辑或警告",
    how_step3_desc: "根据您组织的策略。记录事件。从不存储提示词。",
    features_title: "GuardRok保护什么",
    feat_phi: "患者健康信息", feat_phi_desc: "符合HIPAA/HITECH的PHI阻止。",
    feat_pii: "个人数据", feat_pii_desc: "姓名、电子邮件、ID — GDPR和CCPA合规检测。",
    feat_financial: "财务数据", feat_financial_desc: "账户号码、交易数据 — SOX、FINRA、PCI-DSS覆盖。",
    feat_api: "API密钥和机密", feat_api_desc: "凭据、令牌和密码被拦截。",
    feat_real_estate: "房地产数据", feat_real_estate_desc: "托管详情、电汇指令 — RESPA合规。",
    feat_confidential: "机密内容", feat_confidential_desc: "律师-客户特权、并购数据 — 按策略阻止。",
    stat_block: "实时", stat_block_label: "拦截", stat_tools: "4", stat_tools_label: "支持AI工具",
    stat_store: "0", stat_store_label: "存储的提示词文本", stat_privacy: "< 10分钟", stat_privacy_label: "部署时间",
    cta_title: "今天开始保护您的团队", cta_subtitle: "最多2个席位免费 — 限时入门优惠。无需信用卡。", cta_button: "免费开始",
    footer_tagline: "面向受监管团队的AI防火墙。", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  ko: {
    nav_docs: "문서", nav_use_cases: "사용 사례", nav_pricing: "요금제", nav_signin: "로그인", nav_start_free: "무료로 시작", nav_demo: "데모 예약",
    hero_badge: "IT 설정 불필요 · 10분 내 배포 · 카드 불필요",
    hero_h1_line1: "직원들이 AI에 데이터를",
    hero_h1_line2: "유출하고 있습니다. 지금 막으세요.",
    hero_subtitle: "GuardRok™은 프롬프트가 ChatGPT, Copilot, Gemini 또는 Claude에 도달하기 전에 차단하여 PHI, 개인정보 및 기밀을 실시간으로 보호합니다.",
    hero_cta_primary: "무료로 시작 — 카드 불필요",
    hero_cta_secondary: "작동 방식 보기",
    hero_cta_demo: "데모 예약",
    hero_social_proof: "얼리 액세스 고객 — 잘못된 프롬프트 하나가 제품보다 더 많은 비용을 초래하는 규제된 조직.",
    how_title: "GuardRok 작동 방식",
    how_step1_title: "직원이 프롬프트 입력",
    how_step1_desc: "업무용 브라우저에서 ChatGPT, Copilot, Gemini 또는 Claude에.",
    how_step2_title: "GuardRok이 차단",
    how_step2_desc: "AI 도구에 도달하기 전에 — 실시간으로. 프롬프트는 절대 저장되지 않습니다.",
    how_step3_title: "차단, 수정 또는 경고",
    how_step3_desc: "조직의 정책에 따라. 이벤트를 기록합니다. 프롬프트는 저장하지 않습니다.",
    features_title: "GuardRok이 보호하는 것",
    feat_phi: "환자 건강 정보", feat_phi_desc: "HIPAA/HITECH 준수 PHI 차단.",
    feat_pii: "개인정보", feat_pii_desc: "이름, 이메일, ID — GDPR 및 CCPA 준수 감지.",
    feat_financial: "금융 데이터", feat_financial_desc: "계좌번호, 거래 데이터 — SOX, FINRA, PCI-DSS 적용.",
    feat_api: "API 키 및 비밀", feat_api_desc: "자격증명, 토큰 및 비밀번호 차단.",
    feat_real_estate: "부동산 데이터", feat_real_estate_desc: "에스크로 세부사항, 송금 지시 — RESPA 준수.",
    feat_confidential: "기밀 콘텐츠", feat_confidential_desc: "변호사-의뢰인 특권, M&A 데이터 — 정책으로 차단.",
    stat_block: "실시간", stat_block_label: "차단", stat_tools: "4", stat_tools_label: "지원 AI 도구",
    stat_store: "0", stat_store_label: "저장된 프롬프트 텍스트", stat_privacy: "< 10분", stat_privacy_label: "배포 시간",
    cta_title: "오늘부터 팀을 보호하세요", cta_subtitle: "최대 2석 무료 — 기간 한정 입문 혜택. 신용카드 불필요.", cta_button: "무료로 시작",
    footer_tagline: "규제된 팀을 위한 AI 방화벽.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  hi: {
    nav_docs: "दस्तावेज़", nav_use_cases: "उपयोग मामले", nav_pricing: "मूल्य", nav_signin: "साइन इन", nav_start_free: "मुफ्त शुरू करें", nav_demo: "डेमो बुक करें",
    hero_badge: "कोई IT सेटअप नहीं · 10 मिनट में तैनात · कोई कार्ड नहीं",
    hero_h1_line1: "आपके कर्मचारी AI को डेटा",
    hero_h1_line2: "लीक कर रहे हैं। इसे रोकें।",
    hero_subtitle: "GuardRok™ प्रॉम्प्ट को ChatGPT, Copilot, Gemini या Claude तक पहुँचने से पहले ही रोकता है — PHI, व्यक्तिगत डेटा और रहस्यों को रियल टाइम में ब्लॉक करता है।",
    hero_cta_primary: "मुफ्त शुरू करें — कार्ड की जरूरत नहीं",
    hero_cta_secondary: "यह कैसे काम करता है",
    hero_cta_demo: "डेमो बुक करें",
    hero_social_proof: "अर्ली एक्सेस ग्राहक — विनियमित संगठन जहाँ एक गलत प्रॉम्प्ट उत्पाद से ज्यादा महंगा पड़ता है।",
    how_title: "GuardRok कैसे काम करता है",
    how_step1_title: "कर्मचारी प्रॉम्प्ट टाइप करता है",
    how_step1_desc: "काम के ब्राउज़र पर ChatGPT, Copilot, Gemini या Claude में।",
    how_step2_title: "GuardRok इसे इंटरसेप्ट करता है",
    how_step2_desc: "AI टूल तक पहुँचने से पहले — रियल टाइम में। कोई भी प्रॉम्प्ट कभी स्टोर नहीं होता।",
    how_step3_title: "ब्लॉक, रीडैक्ट या चेतावनी",
    how_step3_desc: "आपके संगठन की नीतियों के आधार पर। घटना लॉग करता है। प्रॉम्प्ट कभी स्टोर नहीं करता।",
    features_title: "GuardRok क्या सुरक्षित करता है",
    feat_phi: "रोगी स्वास्थ्य जानकारी", feat_phi_desc: "HIPAA/HITECH अनुपालन PHI ब्लॉकिंग।",
    feat_pii: "व्यक्तिगत डेटा", feat_pii_desc: "नाम, ईमेल, आईडी — GDPR और CCPA अनुपालन।",
    feat_financial: "वित्तीय डेटा", feat_financial_desc: "खाता संख्या, ट्रेडिंग डेटा — SOX, FINRA, PCI-DSS।",
    feat_api: "API कीज़ और रहस्य", feat_api_desc: "क्रेडेंशियल, टोकन और पासवर्ड इंटरसेप्ट किए।",
    feat_real_estate: "रियल एस्टेट डेटा", feat_real_estate_desc: "एस्क्रो विवरण, वायर ट्रांसफर निर्देश — RESPA।",
    feat_confidential: "गोपनीय सामग्री", feat_confidential_desc: "वकील-क्लाइंट विशेषाधिकार, M&A डेटा — नीति द्वारा अवरुद्ध।",
    stat_block: "रियल टाइम", stat_block_label: "इंटरसेप्शन", stat_tools: "4", stat_tools_label: "AI टूल्स",
    stat_store: "0", stat_store_label: "स्टोर किया गया प्रॉम्प्ट", stat_privacy: "< 10 मिनट", stat_privacy_label: "तैनाती समय",
    cta_title: "आज से अपनी टीम की सुरक्षा शुरू करें", cta_subtitle: "2 सीटों तक मुफ्त — सीमित समय का परिचयात्मक ऑफर। क्रेडिट कार्ड नहीं।", cta_button: "मुफ्त शुरू करें",
    footer_tagline: "विनियमित टीमों के लिए AI फ़ायरवॉल।", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  ar: {
    nav_docs: "التوثيق", nav_use_cases: "حالات الاستخدام", nav_pricing: "الأسعار", nav_signin: "تسجيل الدخول", nav_start_free: "ابدأ مجاناً", nav_demo: "احجز عرضاً",
    hero_badge: "بدون إعداد IT · جاهز خلال 10 دقائق · بدون بطاقة",
    hero_h1_line1: "موظفوك يسرّبون البيانات",
    hero_h1_line2: "إلى الذكاء الاصطناعي. أوقف ذلك.",
    hero_subtitle: "يعترض GuardRok™ المطالبات قبل وصولها إلى ChatGPT أو Copilot أو Gemini أو Claude — يحجب PHI والبيانات الشخصية والأسرار في الوقت الفعلي.",
    hero_cta_primary: "ابدأ مجاناً — لا بطاقة مطلوبة",
    hero_cta_secondary: "اعرف كيف يعمل",
    hero_cta_demo: "احجز عرضاً تجريبياً",
    hero_social_proof: "عملاء الوصول المبكر — مؤسسات خاضعة للتنظيم حيث مطالبة واحدة خاطئة تكلف أكثر من المنتج.",
    how_title: "كيف يعمل GuardRok",
    how_step1_title: "الموظف يكتب مطالبة",
    how_step1_desc: "في ChatGPT أو Copilot أو Gemini أو Claude على متصفح العمل.",
    how_step2_title: "GuardRok يعترضها",
    how_step2_desc: "قبل وصوله إلى أداة الذكاء الاصطناعي — في الوقت الفعلي. لا يُخزَّن أي طلب أبداً.",
    how_step3_title: "حجب أو تنقيح أو تحذير",
    how_step3_desc: "بناءً على سياسات مؤسستك. يسجل الحدث. لا يخزن المطالبة أبداً.",
    features_title: "ما يحميه GuardRok",
    feat_phi: "معلومات صحة المريض", feat_phi_desc: "حجب PHI متوافق مع HIPAA/HITECH.",
    feat_pii: "البيانات الشخصية", feat_pii_desc: "الأسماء والبريد الإلكتروني والمعرفات — متوافق مع GDPR وCCPA.",
    feat_financial: "البيانات المالية", feat_financial_desc: "أرقام الحسابات وبيانات التداول — تغطية SOX وFINRA وPCI-DSS.",
    feat_api: "مفاتيح API والأسرار", feat_api_desc: "بيانات الاعتماد والرموز المميزة وكلمات المرور معترضة.",
    feat_real_estate: "بيانات العقارات", feat_real_estate_desc: "تفاصيل الضمان وتعليمات التحويل — متوافق مع RESPA.",
    feat_confidential: "المحتوى السري", feat_confidential_desc: "امتياز المحامي-العميل وبيانات الاندماج — محجوب بالسياسة.",
    stat_block: "فوري", stat_block_label: "الاعتراض", stat_tools: "4", stat_tools_label: "أدوات AI مدعومة",
    stat_store: "0", stat_store_label: "نص مطالبة مخزّن", stat_privacy: "< 10 دقائق", stat_privacy_label: "وقت النشر",
    cta_title: "ابدأ حماية فريقك اليوم", cta_subtitle: "مجاني لـ 2 مقاعد — عرض تمهيدي لفترة محدودة. بدون بطاقة ائتمان.", cta_button: "ابدأ مجاناً",
    footer_tagline: "جدار حماية AI للفرق الخاضعة للتنظيم.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },

  sw: {
    nav_docs: "Nyaraka", nav_use_cases: "Matumizi", nav_pricing: "Bei", nav_signin: "Ingia", nav_start_free: "Anza bure", nav_demo: "Panga onyesho",
    hero_badge: "Hakuna usanidi wa IT · Inatumika ndani ya dakika 10 · Hakuna kadi",
    hero_h1_line1: "Wafanyakazi wako wanavujisha data",
    hero_h1_line2: "kwa AI. Zuia hilo.",
    hero_subtitle: "GuardRok™ inakamata maombi kabla hayajafika ChatGPT, Copilot, Gemini, au Claude — inazuia PHI, data ya kibinafsi na siri kwa wakati halisi.",
    hero_cta_primary: "Anza bure — hakuna kadi inayohitajika",
    hero_cta_secondary: "Ona jinsi inavyofanya kazi",
    hero_cta_demo: "Panga onyesho",
    hero_social_proof: "Wateja wa upatikanaji wa mapema — mashirika yaliyodhibitiwa ambapo ombi moja baya linagharimu zaidi ya bidhaa.",
    how_title: "Jinsi GuardRok inavyofanya kazi",
    how_step1_title: "Mfanyakazi anaandika ombi",
    how_step1_desc: "Katika ChatGPT, Copilot, Gemini, au Claude kwenye kivinjari cha kazi.",
    how_step2_title: "GuardRok inakamata",
    how_step2_desc: "Kabla haijafika zana ya AI — wakati halisi. Hakuna maombi yanayohifadhiwa.",
    how_step3_title: "Zuia, hariri, au onya",
    how_step3_desc: "Kulingana na sera za shirika lako. Inarekodia tukio. Haihifadhi ombi kamwe.",
    features_title: "Kinachohifadhiwa na GuardRok",
    feat_phi: "Taarifa za Afya za Mgonjwa", feat_phi_desc: "Kuzuia PHI kwa kufuata HIPAA/HITECH.",
    feat_pii: "Data ya Kibinafsi", feat_pii_desc: "Majina, barua pepe, vitambulisho — kufuata GDPR na CCPA.",
    feat_financial: "Data ya Fedha", feat_financial_desc: "Nambari za akaunti, data ya biashara — SOX, FINRA, PCI-DSS.",
    feat_api: "Funguo za API na Siri", feat_api_desc: "Vitambulisho, tokeni na nywila zinazokamatwa.",
    feat_real_estate: "Data ya Mali Isiyohamishika", feat_real_estate_desc: "Maelezo ya amana, maelekezo ya uhamisho — RESPA.",
    feat_confidential: "Maudhui ya Siri", feat_confidential_desc: "Haki ya wakili-mteja, data ya M&A — imezuiwa na sera.",
    stat_block: "Wakati halisi", stat_block_label: "Kukamata", stat_tools: "4", stat_tools_label: "Zana za AI",
    stat_store: "0", stat_store_label: "Maandishi ya ombi yaliyohifadhiwa", stat_privacy: "< 10 dak", stat_privacy_label: "Muda wa usanidi",
    cta_title: "Anza kulinda timu yako leo", cta_subtitle: "Bure kwa viti 2 — ofa ya utangulizi ya muda mfupi. Hakuna kadi ya mkopo.", cta_button: "Anza bure",
    footer_tagline: "Ngome ya AI kwa timu zilizodhibitiwa.", footer_legal: "© 2026 GuardRok™ · aiApas LLC",
  },
}

export default t
